<template>
  <div>
    <Teleport to="body">
      <div v-if="showScanner" class="scanner-fullscreen">
        <QrcodeStream
          v-if="scannerActive"
          :constraints="constraints"
          :formats="['qr_code']"
          @detect="onDetect"
          @error="onError"
        />
        <button class="scanner-close-btn" @click="closeScanner">
          <i class="material-icons">close</i>
        </button>
        <div class="scanner-logo-overlay scanner-logo-top">
          <img src="/img/tolsporA5.png" alt="Logo" />
        </div>
        <button
          v-if="backCameras.length > 1"
          type="button"
          class="scanner-switch-btn"
          @click="showCameraPicker = true"
        >
          <i class="material-icons">flip_camera_android</i>
        </button>
        <div
          v-if="showCameraPicker"
          class="camera-picker-overlay"
          @click.self="showCameraPicker = false"
        >
          <div class="camera-picker-sheet">
            <button
              v-for="(cam, idx) in backCameras"
              :key="cam.deviceId"
              type="button"
              class="camera-picker-item"
              :class="{ active: cam.deviceId === currentDeviceId }"
              @click="selectCamera(cam.deviceId)"
            >
              {{ cam.label || `Camera ${idx + 1}` }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';

export default {
  name: 'CarQrScanner',

  components: {
    QrcodeStream,
  },

  emits: ['decoded'],

  data() {
    return {
      scannerActive: false,
      showScanner: false,
      isIphone: false,
      backCameras: [],
      currentDeviceId: null,
      showCameraPicker: false,
      constraints: {
        facingMode: { exact: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };
  },

  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.isIphone = navigator.userAgent.includes('iPhone');
    if (!this.isIphone) {
      this.constraints = {
        facingMode: 'environment',
        zoom: { ideal: 2 },
        focusMode: ['continuous'],
        width: { ideal: 1280 },
        height: { ideal: 720 },
        aspectRatio: { ideal: 1.7777777778 }
      };
    }
  },

  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.closeScanner();
  },

  methods: {
    handleVisibilityChange() {
      if (document.hidden) {
        this.closeScanner();
      }
    },

    async initializeCamera() {
      try {
        const warmup = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        warmup.getTracks().forEach(t => t.stop());
      } catch (e) {
        delete this.constraints.deviceId;
        this.backCameras = [];
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const labeledDevices = devices
        .filter(device => device.kind === 'videoinput')
        .filter(device => device.label);

      const cameraIndexFromLabel = (label) => {
        const m = label.match(/camera\s+(\d+)/i);
        return m ? Number.parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
      };

      this.backCameras = labeledDevices
        .filter(device => !device.label.toLowerCase().includes('front'))
        .map(d => ({ deviceId: d.deviceId, label: d.label }))
        .sort((a, b) => cameraIndexFromLabel(a.label) - cameraIndexFromLabel(b.label));

      if (labeledDevices.length === 0) {
        delete this.constraints.deviceId;
        this.currentDeviceId = null;
        return;
      }

      const persisted = localStorage.getItem('qrScanner.preferredCameraDeviceId');
      if (persisted && this.backCameras.some(c => c.deviceId === persisted)) {
        this.constraints.deviceId = { exact: persisted };
        this.currentDeviceId = persisted;
        return;
      }

      const exclude = [
        'ultra wide', 'telephoto', 'infrared',
        'macro', 'microscope', 'thermal', 'depth', 'monochrome', 'mono',
        'night vision', 'endoscope',
        'facing front', 'front'
      ];
      const preferredCamera = labeledDevices
        .filter(device => {
          const label = device.label.toLowerCase();
          return !exclude.some(type => label.includes(type));
        })
        .slice()
        .sort((a, b) => cameraIndexFromLabel(a.label) - cameraIndexFromLabel(b.label))[0];
      if (preferredCamera) {
        this.constraints.deviceId = { exact: preferredCamera.deviceId };
        this.currentDeviceId = preferredCamera.deviceId;
      } else {
        delete this.constraints.deviceId;
        this.currentDeviceId = null;
      }
    },

    async selectCamera(deviceId) {
      this.showCameraPicker = false;
      if (!deviceId || deviceId === this.currentDeviceId) return;
      localStorage.setItem('qrScanner.preferredCameraDeviceId', deviceId);
      this.currentDeviceId = deviceId;
      this.constraints = { ...this.constraints, deviceId: { exact: deviceId } };
      this.scannerActive = false;
      await this.$nextTick();
      this.scannerActive = true;
    },

    async openScanner() {
      if (!this.isIphone) {
        await this.initializeCamera();
      }
      this.showScanner = true;
      this.$nextTick(() => {
        this.scannerActive = true;
      });
    },

    closeScanner() {
      this.scannerActive = false;
      this.showScanner = false;
      this.showCameraPicker = false;
    },

    onDetect(detectedCodes) {
      const content = detectedCodes[0]?.rawValue;
      if (!content) return;
      this.$emit('decoded', content);
      this.closeScanner();
    },

    onError(error) {
      console.error('QR scanner error:', error); // eslint-disable-line no-console
    }
  }
};
</script>

<style scoped>
.scanner-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.scanner-fullscreen :deep(> div) {
  width: 100%;
  height: 100%;
}

.scanner-fullscreen :deep(video) {
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  display: block;
}

.scanner-close-btn {
  position: absolute;
  top: calc(20px + env(safe-area-inset-top));
  right: 20px;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  color: #fff;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  font-size: 24px;
}

.scanner-logo-overlay {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0.5;
  pointer-events: none;
}

.scanner-logo-top {
  top: calc(40px + env(safe-area-inset-top));
}

.scanner-logo-overlay img {
  max-width: 250px;
}

.scanner-switch-btn {
  position: absolute;
  bottom: 30px;
  right: 20px;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  color: #fff;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  font-size: 28px;
}

.camera-picker-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.camera-picker-sheet {
  background: #fff;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 500px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
}

.camera-picker-item {
  background: #f5f5f5;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #000;
}

.camera-picker-item.active {
  border-color: #1976d2;
  background: #e3f2fd;
  font-weight: 600;
}

/* ── Dark mode ── */
body.dark-version .camera-picker-sheet { background: #202940; }
body.dark-version .camera-picker-item { background: #1a2035; color: rgba(255,255,255,.85); }
body.dark-version .camera-picker-item.active { background: rgba(25,118,210,.25); border-color: #1976d2; }
</style>
